import axios from "axios"
import _, { get } from "lodash"
import { useState, useContext, useEffect, useMemo } from "react"
import { useLazyQuery } from "@apollo/client"
import { Formik, Form as FormWrapper } from "formik"
import { classNames } from "primereact/utils"
import { Button } from "primereact/button"

// Context
import { UserContext } from "../../../context/userContext"

// Queries
import { PARTICIPANT_BLOCK } from "./queries"

// Helpers
import validationSchema from "../../../helpers/validationSchema/"
import initialValues from "../../../helpers/initialValues"
import {
  normalizePayload,
  transformPayload,
} from "../../../helpers/dateNormalization"
import { cleanMultiFieldValues } from "../../../helpers/cleanMultiFieldValues"

// Comps
import FieldBuilder from "./FieldBuilder"
import { LoaderMedium } from "../../../components/Loaders"
import ParticipantProfileBlocks from "../../FormViewMode/ParticipantProfileBlocks"

// Helpers
import cloudFunctionsEndpoint from "../../../config/cloudFunctionsEndpoint"
import GLOBALS_STATUS from "../../../config/globalsStatus"
import { cleanupTmpFields } from "../../../components/FieldTypes/helpers/cleanupTempFields"
import sanitizeTemplateBlocks from "../../../helpers/sanitizeTemplateBlocks"
import { LogDebug, LogError } from "../../../helpers/logger"

import formStyles from "../../../styles/Form.module.css"
import styles from "../../../styles/Participant-Profile.module.css"

const ALLOWED_EDIT_STATUSES = [
  ...GLOBALS_STATUS.ACTIVE_STATUS,
  GLOBALS_STATUS.KIOSK_MEMBER,
]

export default function Form(props) {
  const [editMode, setEditMode] = useState(false)
  const [fetchData, setFetchData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState(false)

  const userCtx = useContext(UserContext)
  const participant_id = props.participantId

  const [getBlockData] = useLazyQuery(PARTICIPANT_BLOCK, {
    fetchPolicy: "network-only",
    variables: {
      type: "auto",
      organization_id: userCtx.session.user.organization_id,
      enterprise_id: userCtx.session.user.enterprise_id,
      participant_id,
      idBlock: props.blockName,
    },
    onCompleted: (data) => {
      // Sanitize blocks to remove any password fields that shouldn't be rendered
      const sanitizedBlocks = sanitizeTemplateBlocks(props.blockName, [
        data.block,
      ])
      const sanitizedData = { ...data, block: sanitizedBlocks[0] }

      setFetchData(sanitizedData)
      setLoading(false)
    },
    onError: (error) => {
      LogError("Error fetching block data:", error)
      setErrorMessage(
        get(
          error,
          "response.data.message",
          "An error occurred while fetching the block data.",
        ),
      )
      setLoading(false)
    },
  })

  // On load, fetch the block data
  useEffect(() => {
    getBlockData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dataStored = useMemo(
    () => (fetchData ? JSON.parse(fetchData.blockSaved.data) : {}),
    [fetchData],
  )

  const formValues = useMemo(() => {
    if (!fetchData) return {} // Wait for fetchData to be available before computing form values
    const values = {
      ...initialValues(fetchData?.block),
      ...transformPayload(dataStored?.values, { block: fetchData.block }),
    }
    // Remove any multi-field blocks that are completely empty
    cleanMultiFieldValues(values, fetchData?.block?.fields)
    return values
  }, [fetchData, dataStored])

  if (loading || !fetchData || !dataStored || !formValues)
    return <LoaderMedium />

  const handleSubmit = async (values, actions) => {
    const endpoint = cloudFunctionsEndpoint()
    setLoading(true)
    setErrorMessage(null)

    let savedSuccessfully = false
    try {
      // Submit each block
      const postBody = {
        idDoc: dataStored.idDoc,
        organization_alias: userCtx.session.user.organization_alias,
        organization_id: userCtx.session.user.organization_id,
        enterprise_id: userCtx.session.user.enterprise_id,
        enterprise_alias: userCtx.session.user.enterprise_alias,
        participant_alias: userCtx.participant_alias || "not-set",
        participant_membership_id:
          userCtx.participant_membership_id || "not-set",
        participant_id,
        staff_alias: userCtx.session.user.staff_alias,
        staff_id: userCtx.session.user.staff_id,
        block_name: props.blockName,
        values: cleanupTmpFields(values),
      }
      const normalizedBody = normalizePayload(postBody, {
        block: fetchData.block,
      })

      await axios.post(`${endpoint}/blockSubmit`, normalizedBody)
      savedSuccessfully = true
    } catch (error) {
      savedSuccessfully = false
      setErrorMessage(
        get(
          error,
          "response.data.message",
          "An error occurred while submitting the form.",
        ),
      )
      setLoading(false)
    }

    if (savedSuccessfully) {
      // After successful submission, update participant context and refetch data
      userCtx.changeParticipant(participant_id)
      getBlockData()

      // Reset form state and exit edit mode
      setEditMode(!editMode)
    }

    return savedSuccessfully
  }

  const EditButtonToggle = () => {
    return _.includes(
      ALLOWED_EDIT_STATUSES,
      userCtx.participant_membership_status,
    ) ? (
      <Button
        label={editMode ? "View" : "Edit"}
        className={classNames("p-button-sm", styles.editButtonToggle)}
        onClick={() => setEditMode(!editMode)}
      />
    ) : null
  }

  if (!editMode) {
    return (
      <>
        <div className={styles.tabPageHeader}>
          <h1 className="profile-title">
            {_.startCase(props.blockName.replace("__", " ").replace("_", " "))}
          </h1>
          <EditButtonToggle />
        </div>

        <ParticipantProfileBlocks
          idBlock={props.blockName}
          className="profile-tabs-view"
        />
      </>
    )
  }

  return (
    <>
      <div className={styles.tabPageHeader}>
        <h1 className="profile-title">
          {_.startCase(props.blockName.replace("__", " ").replace("_", " "))}
        </h1>
        <EditButtonToggle />
      </div>

      <div className={styles.customSpacedHr} />

      <Formik
        key={props.blockName}
        initialValues={formValues}
        validationSchema={validationSchema([fetchData.block], "block", true)}
        onSubmit={async (values, actions) => {
          const saveRes = await handleSubmit(values, actions)
          if (saveRes) {
            props.triggerParticipantQuery()
            setFetchData(null)
          }
        }}
      >
        <FormWrapper className={formStyles.form} key={props.blockName}>
          <FieldBuilder fields={fetchData.block.fields} />
          {userCtx.participant_membership_status !== "Inactive Discharged" && (
            <button
              type="submit"
              className={classNames("btn", formStyles.btnSubmit)}
              disabled={loading}
            >
              Submit
            </button>
          )}
          {errorMessage && (
            <span
              style={{ marginLeft: "20px", color: "red" }}
              className="error"
            >
              {errorMessage}
            </span>
          )}
        </FormWrapper>
      </Formik>
    </>
  )
}
